import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { createBrowserHistory } from "history";
import { useState, useEffect, ChangeEvent } from "react";
import { Link, useParams } from "react-router-dom";
import ButtonComponent from "../components/ButtonComponent";
import FieldComponent from "../components/FieldComponent";
import Loader from "../components/Loader";
import { useAppDispatch, useAppSelector } from "../hooks/redux-toolkit";
import { useSuccessDispatcher } from "../hooks/useSuccessDispatcher";
import { Actions } from "../misc/access.enum";
import {
  NUMBER,
  MODAL_BUTTON_NAMES,
  CONSTANTS,
  SIZES,
} from "../misc/constants";
import {
  createVINModalVehicleData,
  createVINModalButtons,
} from "../misc/createData";
import { VehicleStatus, FRONTEND_ROUTES } from "../misc/enum";
import { MESSAGES } from "../misc/messages";
import { calculateDays, checkBoolean } from "../misc/utils";
import { toggleProgressBar } from "../store/reducer/progressBar";
import {
  useLazyGetVehicleByIdQuery,
  useSendVehicleToOVEMutation,
  useSendVehicleToRerunMutation,
  useSendVehicleToSecondChanceMutation,
  useUpdateKBBDetailsMutation,
} from "../store/reducer/vehicle";
import { SelectOption, VINModalDataObjectType } from "../ts/component.types";
import {
  IConfigurableOption,
  IConfigurableTrimDetails,
  IVehicleKBBReport,
} from "../ts/kbb.interface";
import SelectComponent from "../components/SelectComponent";

const VehicleDetails = () => {
  const history = createBrowserHistory();
  const dispatch = useAppDispatch();
  const successDispatcher = useSuccessDispatcher();

  const [getVehicleById, { data: vehicleStateData }] =
    useLazyGetVehicleByIdQuery();
  const [sendVehicleToOVE] = useSendVehicleToOVEMutation();
  const [sendVehicleToRerun] = useSendVehicleToRerunMutation();
  const [sendVehicleToSecondChance] = useSendVehicleToSecondChanceMutation();
  const [updateKBBDetails] = useUpdateKBBDetailsMutation();

  const params = useParams();

  const state = useAppSelector((state) => state);
  const { success, permissions } = state.authorization;

  const [vehicleId, setVehicleId] = useState<string>("");
  const [showInput, setShowInput] = useState(false);
  const [ovePrice, setOvePrice] = useState<number>(NUMBER.ZERO);
  const [vehicleDetails, setVehicleDetails] =
    useState<Array<VINModalDataObjectType> | null>(null);
  const [modalButtons, setModalButtons] =
    useState<Array<VINModalDataObjectType> | null>(null);
  const [updateBookSheetButton, setUpdateBookSheetButton] =
    useState<Boolean>(false);
  const [vehicleTitleData, setVehicleTitleData] = useState({
    make: "",
    model: "",
    year: "",
    vin: "",
    stockNumber: "",
  });
  const [kbbReport, setKBBReport] = useState<IVehicleKBBReport | null>(null);
  const [optionStateConfigurable, setOptionStateConfigurable] = useState<
    Array<IConfigurableOption>
  >([]);
  const [initialOptionState, setInitialOptionState] = useState<
    Array<IConfigurableOption>
  >([]);
  const [KBBValues, setKBBValues] = useState<{
    lendingValue: number;
    adjustedLendingValue: number;
    retailValue: number;
    adjustedRetailValue: number;
    mileageAdjustment: number;
  }>({
    lendingValue: 0,
    adjustedLendingValue: 0,
    retailValue: 0,
    adjustedRetailValue: 0,
    mileageAdjustment: 0,
  });
  const [trimSelectionDetails, setTrimSelectionDetails] = useState<{
    initialValue: string;
    value: string;
    selectOptions: Array<SelectOption>;
  }>();
  const [selectionDetails, setSelectionDetails] = useState<{
    engine: {
      initialValue: string;
      value: string;
      selectOptions: Array<SelectOption>;
    };
    transmission: {
      initialValue: string;
      value: string;
      selectOptions: Array<SelectOption>;
    };
    drivetrain: {
      initialValue: string;
      value: string;
      selectOptions: Array<SelectOption>;
    };
  } | null>();

  useEffect(() => {
    (async () => {
      setVehicleId(params?.vehicleId as string);
      const response: any = await getVehicleById(
        (params?.vehicleId as string) + "?kbb=true"
      );
      if (response?.error) {
      }
    })();
  }, []);

  useEffect(() => {
    if (vehicleStateData) {
      const vehicleData = vehicleStateData?.data;
      setVehicleData(vehicleData);
      manageKBBFormValues(vehicleData.kbb_report);
    }
  }, [vehicleStateData]);

  useEffect(() => {
    if (vehicleStateData) {
      const vehicleData = vehicleStateData?.data;
      setVehicleData(vehicleData);
    }
  }, [success]);

  const buttonClick = async (type: string) => {
    if (vehicleId) {
      let response: any;
      let successMessage: string = "";
      dispatch(toggleProgressBar(true));
      switch (type) {
        case MODAL_BUTTON_NAMES.OVE_EVENT:
          setShowInput(!showInput);
          break;
        case MODAL_BUTTON_NAMES.SECOND_CHANCE:
          response = await sendVehicleToSecondChance(vehicleId);
          successMessage = MESSAGES.VIN_MODAL.MOVE_TO_SECOND_CHANCE_SUCCESS;
          break;
        case MODAL_BUTTON_NAMES.RERUN:
          response = await sendVehicleToRerun(vehicleId);
          successMessage = MESSAGES.VIN_MODAL.MOVE_TO_RERUN_SUCCESS;
          break;
        case MODAL_BUTTON_NAMES.SUBMIT_FOR_OVE_EVENT:
          response = await sendVehicleToOVE({
            id: vehicleId,
            payload: {
              price: ovePrice,
            },
          });
          successMessage = MESSAGES.VIN_MODAL.MOVE_TO_OVE_EVENT_SUCCESS;
          break;
        default:
          break;
      }
      dispatch(toggleProgressBar(false));

      if (!response?.error && type !== MODAL_BUTTON_NAMES.OVE_EVENT) {
        successDispatcher(true, successMessage);
      }
    }
  };

  const handleOvePrice = (price: string) => {
    if (!isNaN(Number(price))) {
      setOvePrice(Number(price));
    }
  };

  const setVehicleData = (vehicleData: any) => {
    const hasAuctionCount = vehicleData?.auction_count > NUMBER.ZERO;

    const age = calculateDays(vehicleData?.created_at);

    const vehicleDetails = createVINModalVehicleData(
      `${age} ${age === 1 ? "Day" : "Days"}`,
      vehicleData?.dealer?.name || "N/A",
      vehicleData?.location?.name || "N/A",
      vehicleData?.exterior_color || "N/A",
      "N/A",
      vehicleData?.mileage || "N/A",
      vehicleData?.condition_report?.rating || "N/A",
      vehicleData?.condition_report?.report_url || "",
      vehicleData?.acv || "N/A",
      vehicleData?.floor_price || "N/A",
      vehicleData?.dealer_stock_number || "N/A"
    );

    setVehicleTitleData({
      make: vehicleData?.make,
      model: vehicleData?.model,
      year: vehicleData?.year,
      vin: vehicleData?.vin,
      stockNumber: vehicleData?.stock_number,
    });

    const isInInventory = vehicleData?.status === VehicleStatus.Inventory;
    const modalButtons = createVINModalButtons(
      checkBoolean(permissions?.[Actions.RerunInAuctionButton]) &&
        hasAuctionCount &&
        isInInventory,
      checkBoolean(permissions?.[Actions.SecondChanceAuctionButton]) &&
        hasAuctionCount &&
        isInInventory,
      checkBoolean(permissions?.[Actions.OVEEventSale]) && isInInventory
    );

    setModalButtons(modalButtons);
    setVehicleDetails(vehicleDetails);
    setUpdateBookSheetButton(
      checkBoolean(permissions?.[Actions.AssignAuction])
    );
  };

  const manageKBBFormValues = (kbbReport: IVehicleKBBReport) => {
    setKBBReport(kbbReport);

    const vehicleOptionIds: number[] = kbbReport.vehicleOptionIds.map(
      (v: string) => +v
    );

    const engineOptions: IConfigurableOption[] = [];
    let selectedEngine: number = 0;
    const transmissionOptions: IConfigurableOption[] = [];
    let selectedTransmission: number = 0;
    const drivetrainOptions: IConfigurableOption[] = [];
    let selectedDrivetrain: number = 0;
    const options: IConfigurableOption[] = [];

    kbbReport.vehicleOptions.forEach((opt) => {
      const updatedOption = {
        ...opt,
        checked: vehicleOptionIds.includes(opt.vehicleOptionId),
        value: opt.vehicleOptionId,
        label: opt.optionName,
      };

      if (opt.optionType === "Equipment") {
        // Categorize based on `categoryName`
        if (opt.categoryName === "Engine") {
          engineOptions.push(updatedOption);
          if (updatedOption.checked) {
            selectedEngine = updatedOption.value;
          }
        } else if (opt.categoryName === "Transmission") {
          transmissionOptions.push(updatedOption);
          if (updatedOption.checked) {
            selectedTransmission = updatedOption.value;
          }
        } else if (opt.categoryName === "Drivetrain") {
          drivetrainOptions.push(updatedOption);
          if (updatedOption.checked) {
            selectedDrivetrain = updatedOption.value;
          }
        }
      } else if (opt.optionType === "Option") {
        // Collecting 'Option' type elements
        options.push(updatedOption);
      }
    });

    setInitialOptionState(options);
    setOptionStateConfigurable(options);

    setSelectionDetails({
      engine: {
        value: `${selectedEngine}`,
        initialValue: `${selectedEngine}`,
        selectOptions: engineOptions,
      },
      transmission: {
        value: `${selectedTransmission}`,
        initialValue: `${selectedTransmission}`,
        selectOptions: transmissionOptions,
      },
      drivetrain: {
        value: `${selectedDrivetrain}`,
        initialValue: `${selectedDrivetrain}`,
        selectOptions: drivetrainOptions,
      },
    });

    const trimOptions = kbbReport.vehicleTrims.map((trim) => {
      const updatedTrim = {
        ...trim,
        value: trim.trimId,
        label: trim.trimName,
      };

      return updatedTrim;
    });
    setTrimSelectionDetails({
      value: `${kbbReport.options.trimId}`,
      initialValue: `${kbbReport.options.trimId}`,
      selectOptions: trimOptions,
    });

    const lendingValue =
      kbbReport?.values?.prices?.[0]?.configuredValue?.toFixed(2);
    const adjustedLendingValue =
      lendingValue - kbbReport?.values?.prices?.[0]?.mileageAdjustment;
    const retailValue =
      kbbReport?.values?.prices?.[1]?.configuredValue?.toFixed(2);
    const adjustedRetailValue =
      retailValue - kbbReport?.values?.prices?.[1]?.mileageAdjustment;
    const mileageAdjustment = kbbReport?.values?.prices?.[0]?.mileageAdjustment;
    setKBBValues({
      lendingValue,
      adjustedLendingValue,
      retailValue,
      adjustedRetailValue,
      mileageAdjustment,
    });
  };

  const handleTrimChange = async (selection: any, value: string) => {
    setTrimSelectionDetails((prev: any) => {
      return { ...prev, value };
    });
  };
  const handleEngineChange = async (selection: any, value: string) => {
    setSelectionDetails((prev: any) => {
      return { ...prev, engine: { ...prev?.engine, value } };
    });
  };
  const handleTransmissionChange = async (selection: any, value: string) => {
    setSelectionDetails((prev: any) => {
      return { ...prev, transmission: { ...prev?.transmission, value } };
    });
  };
  const handleDrivetrainChange = async (selection: any, value: string) => {
    setSelectionDetails((prev: any) => {
      return { ...prev, drivetrain: { ...prev?.drivetrain, value } };
    });
  };
  const handleOptionChange = (vehicleOptionId: number, checked: boolean) => {
    setOptionStateConfigurable((prev: any) =>
      prev.map((item: any) =>
        item.vehicleOptionId === vehicleOptionId ? { ...item, checked } : item
      )
    );
  };

  const handleUpdateReport = async () => {
    const selected_option_ids: Array<string | number> = [];
    const deselected_option_ids: Array<string | number> = [];

    for (let i = 0; i < optionStateConfigurable.length; i++) {
      if (optionStateConfigurable[i].checked !== initialOptionState[i].checked) {
        if (optionStateConfigurable[i].checked) {
          selected_option_ids.push(optionStateConfigurable[i].vehicleOptionId);
        } else {
          deselected_option_ids.push(
            optionStateConfigurable[i].vehicleOptionId
          );
        }
      }
    }

    if (selectionDetails) {
      if (
        selectionDetails.engine.value !== selectionDetails.engine.initialValue
      ) {
        selected_option_ids.push(selectionDetails.engine.value);
        deselected_option_ids.push(selectionDetails.engine.initialValue);
      }
      if (
        selectionDetails.transmission.value !==
        selectionDetails.transmission.initialValue
      ) {
        selected_option_ids.push(selectionDetails.transmission.value);
        deselected_option_ids.push(selectionDetails.transmission.initialValue);
      }
      if (
        selectionDetails.drivetrain.value !==
        selectionDetails.drivetrain.initialValue
      ) {
        selected_option_ids.push(selectionDetails.drivetrain.value);
        deselected_option_ids.push(selectionDetails.drivetrain.initialValue);
      }
    }

    if (
      trimSelectionDetails &&
      trimSelectionDetails.initialValue !== trimSelectionDetails.value
    ) {
      selected_option_ids.push(trimSelectionDetails.value);
      deselected_option_ids.push(trimSelectionDetails.initialValue);
    }

    if (selected_option_ids.length || deselected_option_ids.length) {
      dispatch(toggleProgressBar(true));
      const payload = {
        id: vehicleId,
        payload: {
          selected_option_ids,
          deselected_option_ids,
        },
      };
      const response: any = await updateKBBDetails(payload);
      dispatch(toggleProgressBar(false));

      if (!response?.error) {
        manageKBBFormValues(response.data?.data?.kbb_report);
        successDispatcher(true, MESSAGES.VEHICLE.KBB_DETAILS_UPDATED);
      }
    }
  };

  return (
    <div className="background-white px-8 py-4">
      {!vehicleDetails || !kbbReport ? (
        <div className="flex h-[100vh] justify-between">
          <Loader />
        </div>
      ) : (
        <Box className="text-tertiary-200">
          <Box className="mb-2 font-bold text-tertiary-400">
            {vehicleTitleData?.stockNumber} | {vehicleTitleData.year},{" "}
            {vehicleTitleData?.make}, {vehicleTitleData?.model}
          </Box>
          <Box className="flex items-center justify-between mb-2">
            <Box className="text-2xl font-bold">
              VIN: {vehicleTitleData?.vin}
            </Box>
            <Box>
              <Link
                target="_blank"
                to={`${FRONTEND_ROUTES.KBB_REPORT}/${vehicleId}`}
              >
                <ButtonComponent
                  type={CONSTANTS.NEW_WINDOW}
                  text="View KBB Report"
                  color={CONSTANTS.PRIMARY}
                  size={SIZES.SMALL}
                />
              </Link>
              {updateBookSheetButton && (
                <ButtonComponent
                  onClick={handleUpdateReport}
                  type={CONSTANTS.BUTTON}
                  text="Update Booksheet"
                  color={CONSTANTS.TERTIARY}
                  size={SIZES.SMALL}
                />
              )}
            </Box>
          </Box>
          <Box className="flex border-t-2 border-tertiary-300">
            <Box className="border-r-2 p-4 border-tertiary-300 pl-0 w-1/3">
              <Box>
                {vehicleDetails?.map((vehicleDetail, i) => {
                  return (
                    <Box className="flex my-2 justify-between" key={i}>
                      <Box className="w-44 text-left mr-4 flex items-start">
                        {vehicleDetail?.title}:
                      </Box>
                      <Box className="w-40 flex justify-end text-right">
                        {vehicleDetail?.value}
                        {vehicleDetail?.url && (
                          <a
                            href={vehicleDetail?.url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Box className="cursor-pointer ml-4 w-[20px] h-[20px] bg-[url(./assets/images/ExternalLink.png)] bg-no-repeat bg-contain"></Box>
                          </a>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              <Box>
                <div className="border-t-2 border-b-2 mt-8 py-2 border-tertiary-300">
                  <div className="flex justify-between my-1">
                    <div className="">Lending/Retail Value</div>
                    <div className="text-right">
                      ${" " + KBBValues.adjustedLendingValue} / $
                      {" " + KBBValues.adjustedRetailValue}
                    </div>
                  </div>
                  <div className="flex justify-between my-1">
                    <div className="">Mileage Adjustments</div>
                    <div className="text-right">
                      ${" " + KBBValues.mileageAdjustment}
                    </div>
                  </div>
                </div>
                <div className="flex justify-between border-b-4 py-3 border-tertiary-300 font-semibold">
                  <div className="">Final Lending/Retail Value</div>
                  <div className="text-right">
                    ${" " + KBBValues.lendingValue} / $
                    {" " + KBBValues.retailValue}
                  </div>
                </div>
              </Box>
              <Box className="">
                <Box className="flex items-center my-8 gap-2 flex-wrap">
                  {modalButtons?.map((button, i) =>
                    button?.show ? (
                      <ButtonComponent
                        key={i}
                        className={button?.className}
                        size={SIZES.SMALL}
                        color={CONSTANTS.PRIMARY}
                        text={button?.title}
                        onClick={() => buttonClick(button?.type as string)}
                      />
                    ) : null
                  )}
                </Box>
                <FieldComponent
                  className={showInput ? "visible" : "invisible"}
                  placeholder="OVE Event Price"
                  type={CONSTANTS.TICK}
                  value={ovePrice?.toString()}
                  handleChange={(event: ChangeEvent<HTMLInputElement>) =>
                    handleOvePrice(event?.target?.value)
                  }
                  handleIconClick={() =>
                    buttonClick(MODAL_BUTTON_NAMES.SUBMIT_FOR_OVE_EVENT)
                  }
                />
              </Box>
            </Box>
            <Box className="p-4 pr-0 w-full">
              <FormGroup className="">
                <Box className="border-b-2 border-tertiary-300 pb-4 mb-4 flex flex-wrap gap-2">
                  <Box>
                    <Box className="mb-2 font-extrabold text-lg text-tertiary-200">
                      Trim
                    </Box>
                    <SelectComponent
                      className="bg-primary-100 w-60"
                      value={trimSelectionDetails?.value}
                      options={trimSelectionDetails?.selectOptions}
                      hideErrorMessage={true}
                      handleChange={(event: SelectChangeEvent<string>) =>
                        handleTrimChange(
                          trimSelectionDetails,
                          event?.target?.value
                        )
                      }
                    />
                  </Box>
                  <Box>
                    <Box className="mb-2 font-extrabold text-lg text-tertiary-200">
                      Engine
                    </Box>
                    <SelectComponent
                      className="bg-primary-100 w-60"
                      value={selectionDetails?.engine.value}
                      options={selectionDetails?.engine.selectOptions}
                      hideErrorMessage={true}
                      handleChange={(event: SelectChangeEvent<string>) =>
                        handleEngineChange(
                          selectionDetails?.engine,
                          event?.target?.value
                        )
                      }
                    />
                  </Box>
                  <Box>
                    <Box className="mb-2 font-extrabold text-lg text-tertiary-200">
                      Transmission
                    </Box>
                    <SelectComponent
                      className="bg-primary-100 w-60"
                      value={selectionDetails?.transmission.value}
                      options={selectionDetails?.transmission.selectOptions}
                      hideErrorMessage={true}
                      handleChange={(event: SelectChangeEvent<string>) =>
                        handleTransmissionChange(
                          selectionDetails?.transmission,
                          event?.target?.value
                        )
                      }
                    />
                  </Box>
                  <Box>
                    <Box className="mb-2 font-extrabold text-lg text-tertiary-200">
                      Drivetrain
                    </Box>
                    <SelectComponent
                      className="bg-primary-100 w-60"
                      value={selectionDetails?.drivetrain.value}
                      options={selectionDetails?.drivetrain.selectOptions}
                      hideErrorMessage={true}
                      handleChange={(event: SelectChangeEvent<string>) =>
                        handleDrivetrainChange(
                          selectionDetails?.drivetrain,
                          event?.target?.value
                        )
                      }
                    />
                  </Box>
                </Box>
                <Box className="mb-2 font-extrabold text-lg text-tertiary-200">
                  Options
                </Box>
                <Box className="flex -1-1 flex-row flex-wrap">
                  {optionStateConfigurable.map((item: any) => (
                    <FormControlLabel
                      key={item.vehicleOptionId}
                      className="w-60"
                      control={
                        <Checkbox
                          checked={item.checked}
                          onChange={(e) =>
                            handleOptionChange(
                              item.vehicleOptionId,
                              e.target.checked
                            )
                          }
                          name={item.label}
                        />
                      }
                      label={<Typography>{item.label}</Typography>}
                    />
                  ))}
                </Box>
              </FormGroup>
            </Box>
          </Box>
        </Box>
      )}
    </div>
  );
};

export default VehicleDetails;
